import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import DecidedBy from "../../components/common/DecidedBy";
import TestDataBadge from "../../components/common/TestDataBadge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Skeleton } from "../../components/ui/skeleton";
import { Sparkles, CheckCircle2, Undo2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { monthLabelIST } from "../../lib/datetime";
import { joinParts, plural } from "../../lib/vocabulary";
import { plannedDoctorsLabel } from "./plannedDoctors";
import StickyHeading from "../../components/common/StickyHeading";

// One row per action kind, so the dialog's title, its button and the toast
// cannot describe three different things. The kind IS the endpoint segment.
const ACTION_TITLE = { approve: "Approve plan", "send-back": "Send back plan", reopen: "Reopen plan" };
const ACTION_CTA = { approve: "Approve", "send-back": "Send back", reopen: "Reopen" };
const ACTION_DONE = {
  approve: "Plan approved",
  "send-back": "Plan sent back",
  reopen: "Plan reopened for editing",
};
const ACTION_PLACEHOLDER = {
  approve: "Optional remark",
  "send-back": "Reason / changes needed (required)",
  reopen: "Why the plan is being reopened (required)",
};

export default function TeamTourPlanPage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionMode, setActionMode] = useState(null); // {plan, kind}
  const [remark, setRemark] = useState("");
  const [acting, setActing] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/tour-plans");
      setPlans(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function submitAction() {
    if (!actionMode) return;
    // Reopen takes the send-back rule: an approved month is being taken back
    // off the officer's record, and the reason is the only thing that tells
    // them why it moved.
    if (actionMode.kind !== "approve" && !remark.trim()) {
      toast.error("Add a remark");
      return;
    }
    setActing(true);
    try {
      await api.put(`/tour-plans/${actionMode.plan.id}/${actionMode.kind}`, { remark: remark.trim() || null });
      toast.success(ACTION_DONE[actionMode.kind]);
      setActionMode(null);
      setRemark("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setActing(false);
    }
  }

  // Own plan is visible here but is not an approval task — see Mine.
  const submitted = plans.filter(
    (p) => p.status === "Submitted" && p.sales_officer_id !== user?.id
  );
  const decided = plans.filter((p) => p.status !== "Submitted" && p.status !== "Draft");

  return (
    <div className="space-y-6" data-testid="team-tour-plan-page">
      <StickyHeading
        icon={Sparkles}
        label="Team Tour Plans"
        title="Team plans"
        subtitle={<>{plural(submitted.length, "plan")} pending approval</>}
        testId="team-tour-plan-heading"
      />

      {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}

      <section>
        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-3">
          Pending approval · {submitted.length}
        </h2>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
          </div>
        ) : submitted.length === 0 ? (
          <div className="bg-white border border-callisto rounded-xl p-6 text-center text-sm text-callisto-muted">
            All caught up.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="team-tour-plan-pending">
            {submitted.map((p) => <PlanCard key={p.id} plan={p} setActionMode={setActionMode} />)}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-3">
          Decided · {decided.length}
        </h2>
        {decided.length === 0 ? (
          <div className="bg-white border border-callisto rounded-xl p-6 text-center text-sm text-callisto-muted">
            No decisions yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {/* An APPROVED plan is where Reopen lives, and it sits here. The
                card was rendered without `setActionMode`, so the decided list
                had no actions at all. */}
            {decided.map((p) => <PlanCard key={p.id} plan={p} setActionMode={setActionMode} />)}
          </div>
        )}
      </section>

      <Dialog open={!!actionMode} onOpenChange={(o) => { if (!o) { setActionMode(null); setRemark(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{ACTION_TITLE[actionMode?.kind]}</DialogTitle>
            <DialogDescription>
              {actionMode?.plan?.sales_officer_name} · {actionMode && monthLabelIST(actionMode.plan.year, actionMode.plan.month)}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            rows={3}
            placeholder={ACTION_PLACEHOLDER[actionMode?.kind]}
            className="focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="team-tour-plan-remark"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionMode(null); setRemark(""); }}>Cancel</Button>
            <Button
              onClick={submitAction}
              disabled={acting}
              className={
                actionMode?.kind === "approve" ? "bg-emerald-600 hover:bg-emerald-700"
                : actionMode?.kind === "reopen" ? "bg-amber-600 hover:bg-amber-700"
                : "bg-red-600 hover:bg-red-700"
              }
              data-testid="team-tour-plan-confirm"
            >
              {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : ACTION_CTA[actionMode?.kind]}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PlanCard({ plan, setActionMode }) {
  return (
    <div className="bg-white border border-callisto rounded-xl p-4" data-testid={`tour-plan-card-${plan.id}`}>
      <Link to={`/route-planner/${plan.id}`} className="block group">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-bold text-callisto-navy group-hover:underline truncate">
            {plan.sales_officer_name}
          </h3>
          <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${
            plan.status === "Approved" ? "bg-emerald-50 text-emerald-700"
            : plan.status === "Submitted" ? "bg-amber-50 text-amber-700"
            : "bg-slate-100 text-slate-700"
          }`}>{plan.status}</span>
        </div>
        <p className="mt-1 text-xs text-callisto-muted">
          {joinParts([
            monthLabelIST(plan.year, plan.month),
            `${plural(plan.daily_plan?.length || 0, "day")} planned`,
            // Not `0 doctors` when the plan never named any — see ./plannedDoctors.
            plannedDoctorsLabel(plan),
          ])}
        </p>
        <TestDataBadge record={plan} className="mt-1" />
        <DecidedBy record={plan} className="mt-1" />
        {plan.approver_remark && (
          <p className="mt-1.5 text-[11px] italic text-callisto-muted">"{plan.approver_remark}"</p>
        )}
      </Link>
      {setActionMode && plan.can_approve && plan.status === "Submitted" && (
        <div className="mt-3 flex gap-2">
          <Button
            size="sm"
            onClick={() => setActionMode({ plan, kind: "approve" })}
            className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-[11px]"
            data-testid={`team-tour-plan-approve-${plan.id}`}
          >
            <CheckCircle2 size={12} className="mr-1" />
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setActionMode({ plan, kind: "send-back" })}
            className="h-8 text-[11px] border-red-200 text-red-700 hover:bg-red-50"
            data-testid={`team-tour-plan-sendback-${plan.id}`}
          >
            <Undo2 size={12} className="mr-1" />
            Send back
          </Button>
        </div>
      )}
      {/* `can_reopen`, NOT `can_approve`: the latter is the STAGE gate, so on
          a decided plan it names only whoever signed last and hides Reopen
          from the officer's own Sales Manager. The server derives this one
          from chain membership — see `team_scope.can_reopen_submission`. */}
      {setActionMode && plan.can_reopen && (
        <div className="mt-3 flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setActionMode({ plan, kind: "reopen" })}
            className="h-8 text-[11px] border-amber-200 text-amber-700 hover:bg-amber-50"
            data-testid={`team-tour-plan-reopen-${plan.id}`}
          >
            <Undo2 size={12} className="mr-1" />
            Reopen
          </Button>
        </div>
      )}
    </div>
  );
}
